import { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/api';
import { User } from '../types';
import { useAuth } from '../contexts/AuthContext';

// Cache sistemi
const cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
const CACHE_TTL = 30000; // 30 saniye

const getCachedData = (key: string) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < cached.ttl) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCachedData = (key: string, data: any, ttl = CACHE_TTL) => {
  cache.set(key, { data, timestamp: Date.now(), ttl });
};

export const useOnlineUsers = () => {
  const [onlineUsers, setOnlineUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchRef = useRef<number>(0);
  const MIN_FETCH_INTERVAL = 5000; // Minimum 5 saniye aralık

  const fetchAllUsers = useCallback(async () => {
    // Rate limiting kontrolü
    const now = Date.now();
    if (now - lastFetchRef.current < MIN_FETCH_INTERVAL) {
      return;
    }
    lastFetchRef.current = now;

    try {

      // Cache kontrolü
      const cacheKey = `users-${user?.role}`;
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        setOnlineUsers(cachedData);
        setLoading(false);
        return;
      }

      // Sadece admin/superadmin kullanıcıları için users endpoint'ini çağır
      const allUsersData = await (['admin', 'superadmin'].includes(user?.role || ''))
        ? apiService.getUsers().catch((err) => {
            console.error('❌ [useOnlineUsers] Error fetching all users:', err);
            return [];
          })
        : Promise.resolve([]);

      const onlineUsersData = await apiService.getOnlineUsers().catch((err) => {
        console.error('❌ [useOnlineUsers] Error fetching online users:', err);
        return [];
      });

      const usersArray: User[] = Array.isArray(allUsersData) ? allUsersData : [];
      const onlineUsersArray: User[] = Array.isArray(onlineUsersData) ? onlineUsersData : [];


      // Online kullanıcıların ID'lerini al
      const onlineUserIds = new Set(onlineUsersArray.map((u: User) => u.id));
      // last_activity map'i oluştur
      const lastActivityMap = new Map<number, string>();
      onlineUsersArray.forEach((u: User) => {
        if (u.last_activity) lastActivityMap.set(u.id, u.last_activity);
      });

      // Eğer allUsers boşsa, online users endpoint'inden gelen tüm kullanıcıları kullan
      if (usersArray.length === 0) {

        const filteredUsers = onlineUsersArray
          .filter((u: User) => {
            const isCurrentUser = u.id === user?.id;
            const isActive = u.is_active !== false; // undefined veya true ise aktif kabul et
            const roleMatch = ['admin', 'superadmin'].includes(user?.role || '') 
              ? ['admin', 'superadmin', 'teknisyen'].includes(u.role)
              : ['admin', 'superadmin'].includes(u.role);
            
            
            return !isCurrentUser && isActive && roleMatch;
          })
          .sort((a: User, b: User) => {
            // Online olanları önce göster, sonra alfabetik sırala
            if (a.is_online && !b.is_online) return -1;
            if (!a.is_online && b.is_online) return 1;
            return a.name.localeCompare(b.name);
          });
        
        setCachedData(cacheKey, filteredUsers);
        setOnlineUsers(filteredUsers);
        return;
      }

      // Eğer kullanıcı admin veya superadmin ise admin/superadmin/teknisyen kullanıcıları göster, değilse sadece admin/superadmin'leri
      const filteredUsers = usersArray
        .filter((u: User) => {
          if (u.id === user?.id || !u.is_active) return false;

          // Eğer mevcut kullanıcı admin/superadmin ise admin/superadmin/teknisyen kullanıcıları göster
          if (['admin', 'superadmin'].includes(user?.role || '')) {
            return ['admin', 'superadmin', 'teknisyen'].includes(u.role);
          }

          // Değilse sadece admin/superadmin'leri göster
          return ['admin', 'superadmin'].includes(u.role);
        })
        .map((u: User) => ({
          ...u,
          is_online: onlineUserIds.has(u.id),
          last_activity: lastActivityMap.get(u.id) || u.last_activity
        }))
        // Tüm kullanıcıları göster (offline olanlar da dahil)
        .sort((a: User, b: User) => {
          // Online olanları önce göster, sonra alfabetik sırala
          if (a.is_online && !b.is_online) return -1;
          if (!a.is_online && b.is_online) return 1;
          return a.name.localeCompare(b.name);
        });

      setCachedData(cacheKey, filteredUsers);
      setOnlineUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role, user?.id]);

  useEffect(() => {
    if (user) {
      // Debounced fetch
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      
      fetchTimeoutRef.current = setTimeout(() => {
        fetchAllUsers();
      }, 100);

      // Poll every 60 seconds for users (increased from 30s to reduce load)
      const interval = setInterval(fetchAllUsers, 60000);

      // Send heartbeat every 45 seconds
      const heartbeatInterval = setInterval(() => {
        apiService.sendHeartbeat().catch(console.error);
      }, 45000);

      return () => {
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
        }
        clearInterval(interval);
        clearInterval(heartbeatInterval);
      };
    }
  }, [user, fetchAllUsers]);

  return { onlineUsers, loading, refresh: fetchAllUsers };
};